import { Table } from 'antd';
import React, { CSSProperties } from 'react';
import styled from 'styled-components';

import { ItemType } from '../../types';

type ProjectResourceProps = {
    item: ItemType;
    style?: CSSProperties;
};

type ResourceRowType = {
    key: string;
    resource: string;
    count: number | undefined | null;
    hours: number | undefined | null;
    total: string;
    className: string;
};

const StyledTable = styled(Table<ResourceRowType>)`
    .people-row {
        background-color: #e9f4ff;
    }

    .equipment-row {
        background-color: #ffffe5;
    }

    .material-row {
        background-color: #e5ffe5;
    }

    .ant-table-tbody > tr > td {
        height: 32px;
        padding-top: 0;
        padding-bottom: 0;
    }

    .ant-table-thead > tr > th {
        height: 32px;
        padding-top: 0;
        padding-bottom: 0;
    }

    .ant-table-footer {
        height: 32px;
        padding-top: 0;
        padding-bottom: 0;
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }

    ,
    .total-row {
        font-weight: bold;
        background-color: #fafafa;

        td:first-child {
            border-bottom-left-radius: 8px;
        }

        td:last-child {
            border-bottom-right-radius: 8px;
        }
    }

    .ant-table-container {
        border-radius: 8px;
        overflow: hidden;
    }
`;

const formatCurrency = (amount: number | undefined | null): string => {
    if (!amount && amount !== 0) {
        return '';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
    }).format(amount);
};

export const ProjectOrPhaseResource: React.FC<ProjectResourceProps> = ({ item, style }) => {
    const totalValueOutput =
        (item.valueTotal ?? 0) +
        (item.valueLabour ?? 0) +
        (item.valueMaterial ?? 0) +
        (item.valueEquipment ?? 0) +
        (item.valueOther ?? 0);

    const dataSource: ResourceRowType[] = [
        {
            key: '1',
            resource: 'People',
            count: item.countPeople,
            hours: item.countHours,
            total: formatCurrency(item.valueTotal),
            className: 'people-row',
        },
        {
            key: '2',
            resource: 'People (labour)',
            count: null,
            hours: null,
            total: formatCurrency(item.valueLabour),
            className: 'people-row',
        },
        {
            key: '3',
            resource: 'People (other)',
            count: null,
            hours: null,
            total: formatCurrency(item.valueOther),
            className: 'people-row',
        },
        {
            key: '4',
            resource: 'Equipment',
            count: item.countTools,
            hours: item.equipmentHours,
            total: formatCurrency(item.valueEquipment),
            className: 'equipment-row',
        },
        {
            key: '5',
            resource: 'Material',
            count: item.countMaterial,
            hours: item.materialHours,
            total: formatCurrency(item.valueMaterial),
            className: 'material-row',
        },
        {
            key: 'total',
            resource: 'Total',
            count: null,
            hours: null,
            total: formatCurrency(totalValueOutput),
            className: 'total-row',
        },
    ];

    const columns = [
        {
            title: 'Resources',
            dataIndex: 'resource',
            key: 'resource',
            onCell: (record: ResourceRowType) => {
                if (record.key === 'total') {
                    return {
                        colSpan: 3, // Merge the first 3 columns
                        style: { textAlign: 'right', fontWeight: 'bold' },
                    };
                }
                return {};
            },
        },
        {
            title: 'Count',
            dataIndex: 'count',
            key: 'count',
            onCell: (record: ResourceRowType) => {
                if (record.key === 'total') {
                    return { colSpan: 0 };
                }
                return {};
            },
        },
        {
            title: 'Hours',
            dataIndex: 'hours',
            key: 'hours',
            onCell: (record: ResourceRowType) => {
                if (record.key === 'total') {
                    return { colSpan: 0 };
                }
                return {};
            },
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
        },
    ];

    return (
        <StyledTable
            style={style}
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            bordered
            rowClassName={(record) => record.className}
        />
    );
};
